import { useState, useEffect, useRef, useCallback } from 'react';
import { Device, Call } from '@twilio/voice-sdk';
import { supabase } from '../lib/supabase';

export interface TwilioDeviceState {
  device: Device | null;
  activeCall: Call | null;
  status: 'idle' | 'connecting' | 'ready' | 'offline' | 'error';
  error: string | null;
  isMuted: boolean;
  isOnHold: boolean;
  callState: 'idle' | 'connecting' | 'ringing' | 'connected' | 'disconnecting';
  incomingCallSid: string | null;
  queueName: string | null;
  companyName: string | null;
}

export interface TwilioDeviceActions {
  initializeDevice: (identity: string) => Promise<void>;
  makeCall: (phoneNumber: string, options?: { fromNumber?: string; agentId?: string }) => Promise<void>;
  acceptCall: () => void;
  rejectCall: () => void;
  hangUp: () => void;
  toggleMute: () => void;
  toggleHold: () => void;
  sendDigits: (digits: string) => void;
}

export const useTwilioDevice = (): [TwilioDeviceState, TwilioDeviceActions] => {
  const [state, setState] = useState<TwilioDeviceState>({
    device: null,
    activeCall: null,
    status: 'idle',
    error: null,
    isMuted: false,
    isOnHold: false,
    callState: 'idle',
    incomingCallSid: null,
    queueName: null,
    companyName: null,
  });

  const deviceRef = useRef<Device | null>(null);
  const activeCallRef = useRef<Call | null>(null);
  const attachedRef = useRef<boolean>(false);
  const isOnHoldRef = useRef<boolean>(false); // Persistent hold state immune to re-renders
  const preHoldMuteStateRef = useRef<boolean>(false); // Store mute state before hold
  const conferenceNameRef = useRef<string | null>(null); // Track conference name after migration
  const isMigratingRef = useRef<boolean>(false); // Prevent duplicate migrations
  const [isConferenceReady, setIsConferenceReady] = useState<boolean>(false); // Track if conference migration is complete

  

  // Initialize the Twilio Device
  const initializeDevice = useCallback(async (identity: string) => {
    try {
      setState(prev => ({ ...prev, status: 'connecting', error: null }));

      // If a global device already exists, attach and return
      const existing = (window as any).__twilioDevice as Device | undefined;
      if (existing) {
        deviceRef.current = existing;
        registerDeviceListeners(existing);
        setState(prev => ({ ...prev, device: existing, status: 'ready' }));
        return;
      }

      // Get token from edge function
      const { data: authData } = await supabase.auth.getSession();
      if (!authData.session) {
        throw new Error('Not authenticated');
      }

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-twilio-token`;
      const options: RequestInit = {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authData.session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identity }),
      };
      const response = await fetch(url, options);

      if (!response.ok) {
        throw new Error('Failed to get Twilio token');
      }

      const { token } = await response.json();

      // Initialize Device with ringtone
      const device = new Device(token, {
        logLevel: 1,
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
        sounds: {
          incoming: 'https://sdk.twilio.com/js/client/sounds/releases/1.0.0/incoming.mp3',
        },
      });

      // Use a global singleton so multiple hooks share the same Device
      (window as any).__twilioDevice = device;

      // Set up device event listeners (once per hook instance)
      registerDeviceListeners(device);

      // Assign device ref before registering
      deviceRef.current = device;

      // Register device
      await device.register();
    } catch (error) {
      console.error('Failed to initialize device:', error);
      setState(prev => ({
        ...prev,
        status: 'error',
        error: error instanceof Error ? error.message : 'Failed to initialize device',
      }));
    }
  }, []);

  // Set up call event listeners
  const setupCallListeners = useCallback((call: Call) => {
    call.on('accept', async () => {
      console.log('Call accepted');
      setState(prev => ({ ...prev, callState: 'connected' }));
      
      // For outbound calls, we need to set up a conference to enable hold/transfer
      const callSid = (call.parameters as any)?.CallSid;
      const direction = (call.parameters as any)?.Direction;
      
      console.log('[Conference Setup] Call direction:', direction);
      console.log('[Conference Setup] CallSid:', callSid);
      console.log('[Conference Setup] Current conferenceNameRef:', conferenceNameRef.current);
      
      // If this is an outbound call and conference isn't set up yet, create one
      if (direction === 'outbound' && !conferenceNameRef.current && callSid) {
        console.log('[Conference Setup] Setting up conference for outbound call');
        
        try {
          const { data: authData } = await supabase.auth.getSession();
          if (authData.session) {
            const confName = `conf-${callSid}`;
            console.log('[Conference Setup] Creating conference:', confName);
            
            // Migrate this call to a conference
            const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/twilio-migrate-to-conference`;
            const response = await fetch(url, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${authData.session.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                callSid: callSid,
                conferenceName: confName,
              }),
            });
            
            if (response.ok) {
              conferenceNameRef.current = confName;
              console.log('[Conference Setup] ✅ Conference created for outbound call:', confName);
              console.log('[Conference Setup] Hold/Transfer buttons are now ENABLED');
            } else {
              const error = await response.text();
              console.error('[Conference Setup] ❌ Failed to create conference:', error);
            }
          }
        } catch (error) {
          console.error('[Conference Setup] ❌ Error setting up conference:', error);
        }
      } else if (conferenceNameRef.current) {
        console.log('[Conference Setup] Conference already set:', conferenceNameRef.current);
      }
    });

    call.on('disconnect', async () => {
      console.log('Call disconnected');
      
      // CRITICAL: When agent hangs up, end the conference to disconnect customer
      if (conferenceNameRef.current) {
        try {
          const { data: authData } = await supabase.auth.getSession();
          if (authData.session) {
            console.log('[Disconnect] Ending conference:', conferenceNameRef.current);
            
            const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/twilio-end-conference`;
            await fetch(url, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${authData.session.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                conferenceName: conferenceNameRef.current,
              }),
            });
            
            console.log('[Disconnect] Conference ended successfully');
          }
        } catch (error) {
          console.error('[Disconnect] Failed to end conference:', error);
        }
      }
      
      activeCallRef.current = null;
      conferenceNameRef.current = null;
      isMigratingRef.current = false;
      setIsConferenceReady(false);
      
      // Clear hold state
      isOnHoldRef.current = false;
      
      setState(prev => ({
        ...prev,
        activeCall: null,
        callState: 'idle',
        isMuted: false,
        isOnHold: false,
        incomingCallSid: null,
      }));
    });

    call.on('reconnecting', (error) => {
      console.log('Call reconnecting:', error);
      // Don't reset isOnHold during reconnection
    });

    call.on('reconnected', () => {
      console.log('Call reconnected');
      // Don't reset isOnHold after reconnection
    });

    call.on('cancel', () => {
      console.log('Call cancelled');
      activeCallRef.current = null;
      setState(prev => ({
        ...prev,
        activeCall: null,
        callState: 'idle',
        incomingCallSid: null,
      }));
    });

    call.on('reject', () => {
      console.log('Call rejected');
      activeCallRef.current = null;
      setState(prev => ({
        ...prev,
        activeCall: null,
        callState: 'idle',
        incomingCallSid: null,
      }));
    });

    call.on('error', (error) => {
      console.error('Call error:', error);
      setState(prev => ({
        ...prev,
        error: error.message || 'Call error occurred',
      }));
    });

    call.on('mute', (isMuted) => {
      console.log('Call mute status:', isMuted);
      setState(prev => ({ ...prev, isMuted }));
    });
  }, []);

  const registerDeviceListeners = useCallback((device: Device) => {
    if (attachedRef.current) return;
    attachedRef.current = true;

    device.on('registered', () => {
      console.log('Twilio Device registered');
      setState(prev => ({ ...prev, status: 'ready', error: null, device }));
    });

    device.on('error', (error) => {
      console.error('Twilio Device error:', error);
      setState(prev => ({
        ...prev,
        status: 'error',
        error: (error as any).message || 'Device error occurred',
      }));
    });

    device.on('incoming', (call: Call) => {
      const anyCall = call as any;
      console.log('[Incoming Call] From:', anyCall?.parameters?.From);
      console.log('[Incoming Call] CallSid:', anyCall?.parameters?.CallSid);
      console.log('[Incoming Call] All parameters:', anyCall?.parameters);
      console.log('[Incoming Call] Custom parameters:', anyCall?.customParameters);
      console.log('[Incoming Call] isOnHoldRef.current:', isOnHoldRef.current);
      
      // Extract conference name and ParentCallSid from parameters
      // These are passed in the To field as query params: client:agent-X?ParentCallSid=Y&ConfName=Z&QueueName=A&CompanyName=B
      let parentCallSid = anyCall?.parameters?.ParentCallSid;
      let confName = anyCall?.parameters?.ConfName;
      let queueName = anyCall?.parameters?.QueueName;
      let companyName = anyCall?.parameters?.CompanyName;
      
      // Try customParameters Map first
      if (!parentCallSid && anyCall?.customParameters) {
        try {
          parentCallSid = anyCall.customParameters.get('ParentCallSid');
          confName = anyCall.customParameters.get('ConfName');
          queueName = anyCall.customParameters.get('QueueName');
          companyName = anyCall.customParameters.get('CompanyName');
          console.log('[Incoming Call] From customParameters - ParentCallSid:', parentCallSid, 'ConfName:', confName, 'QueueName:', queueName, 'CompanyName:', companyName);
        } catch (e) {
          console.log('[Incoming Call] Error accessing customParameters:', e);
        }
      }
      
      // Also check if it's in the Params query string
      if ((!parentCallSid || !confName || !queueName || !companyName) && anyCall?.parameters?.Params) {
        try {
          const params = new URLSearchParams(anyCall.parameters.Params);
          if (!parentCallSid) {
            parentCallSid = params.get('ParentCallSid') || undefined;
          }
          if (!confName) {
            confName = params.get('ConfName') || undefined;
          }
          if (!queueName) {
            queueName = params.get('QueueName') || undefined;
          }
          if (!companyName) {
            companyName = params.get('CompanyName') || undefined;
          }
          console.log('[Incoming Call] From Params string - ParentCallSid:', parentCallSid, 'ConfName:', confName, 'QueueName:', queueName, 'CompanyName:', companyName);
        } catch (e) {
          console.log('[Incoming Call] Error parsing Params:', e);
        }
      }
      
      // CRITICAL FIX: Parse the To parameter directly to extract query parameters
      // Twilio passes query params in the To field: client:agent-X?Param=Value
      if ((!parentCallSid || !confName || !queueName || !companyName) && anyCall?.parameters?.To) {
        try {
          const toParam = anyCall.parameters.To as string;
          console.log('[Incoming Call] Parsing To parameter:', toParam);
          
          // Extract query string from To parameter (everything after ?)
          const queryStringIndex = toParam.indexOf('?');
          if (queryStringIndex !== -1) {
            const queryString = toParam.substring(queryStringIndex + 1);
            console.log('[Incoming Call] Found query string:', queryString);
            
            const params = new URLSearchParams(queryString);
            if (!parentCallSid) {
              parentCallSid = params.get('ParentCallSid') || undefined;
            }
            if (!confName) {
              confName = params.get('ConfName') || undefined;
            }
            if (!queueName) {
              queueName = params.get('QueueName') || undefined;
            }
            if (!companyName) {
              companyName = params.get('CompanyName') || undefined;
            }
            console.log('[Incoming Call] ✅ Parsed from To parameter - ParentCallSid:', parentCallSid, 'ConfName:', confName, 'QueueName:', queueName, 'CompanyName:', companyName);
          }
        } catch (e) {
          console.log('[Incoming Call] Error parsing To parameter:', e);
        }
      }
      
      // CRITICAL: Set conference name for hold/transfer functionality
      if (confName) {
        conferenceNameRef.current = confName;
        console.log('[Conference Setup] Conference name set:', confName);
        console.log('[Conference Setup] Hold/Transfer buttons are now ENABLED');
      } else {
        console.warn('[Conference Setup] No ConfName found - Hold/Transfer will be disabled');
        conferenceNameRef.current = null;
      }

      // Fast server-side lookup for queue/company.
      // 1) Try RPC (security definer) to bypass RLS cleanly
      // 2) Fallback to edge function calls-lookup
      const callSid = anyCall?.parameters?.CallSid;
      if (callSid && (!queueName || !companyName)) {
        (async () => {
          try {
            const { data: authData } = await supabase.auth.getSession();
            if (!authData.session) return;

            // 1) RPC first
            try {
              const { data: rpcData, error: rpcError } = await supabase.rpc('get_call_queue_company', {
                p_call_sid: callSid,
                p_to_number: null
              });

              if (!rpcError && rpcData && (rpcData.queue_name || rpcData.company_name)) {
                const resolvedQueue = rpcData.queue_name ?? null;
                const resolvedCompany = rpcData.company_name ?? null;

                if (!queueName && resolvedQueue) queueName = resolvedQueue;
                if (!companyName && resolvedCompany) companyName = resolvedCompany;

                setState(prev => ({
                  ...prev,
                  queueName: (resolvedQueue ?? prev.queueName) || null,
                  companyName: (resolvedCompany ?? prev.companyName) || null,
                }));
                console.log('[Incoming Call] RPC get_call_queue_company resolved names:', rpcData);
                return; // done
              } else if (rpcError) {
                console.warn('[Incoming Call] RPC get_call_queue_company error:', rpcError);
              }
            } catch (rpcCatch) {
              console.warn('[Incoming Call] RPC get_call_queue_company exception:', rpcCatch);
            }

            // 2) Edge function fallback
            try {
              const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calls-lookup`, {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${authData.session.access_token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ callSid }),
              });
              if (res.ok) {
                const payload = await res.json();
                if (!queueName && payload.queueName) {
                  queueName = payload.queueName;
                }
                if (!companyName && payload.companyName) {
                  companyName = payload.companyName;
                }
                setState(prev => ({
                  ...prev,
                  queueName: (payload.queueName ?? prev.queueName) || null,
                  companyName: (payload.companyName ?? prev.companyName) || null,
                }));
                console.log('[Incoming Call] calls-lookup resolved names:', payload);
              } else {
                const txt = await res.text();
                console.warn('[Incoming Call] calls-lookup non-200:', txt);
              }
            } catch (efCatch) {
              console.warn('[Incoming Call] calls-lookup error:', efCatch);
            }
          } catch (e) {
            console.warn('[Incoming Call] server-side lookup (rpc/edge) error:', e);
          }
        })();
      }
      
      // 🔥 CRITICAL FIX: Query database for call details since Twilio strips query params
      // The backend stores queue_name and company_name in the calls table
      if (callSid && !queueName && !companyName) {
        console.log('[Incoming Call] 🔍 Query database for call details, CallSid:', callSid);
        (async () => {
          try {
            // Retry fetching the call row briefly to handle race with insert (Twilio event arrives before DB write)
            let callRow: any = null;
            let toNumber: string | null = null;
            let dbQueueName: string | undefined = queueName || undefined;
            let dbCompanyName: string | undefined = companyName || undefined;

            const sleep = (ms: number) => new Promise(res => setTimeout(res, ms));

            for (let attempt = 1; attempt <= 5; attempt++) {
              const { data, error } = await supabase
                .from('calls')
                .select('queue_name, company_name, to_number')
                .eq('call_sid', callSid)
                .maybeSingle();

              if (error) {
                // 406 typically means no row yet when using maybeSingle; wait and retry
                console.warn(`[Incoming Call] Attempt ${attempt}: calls lookup not ready (code may be 406). Retrying...`, error);
              } else if (data) {
                callRow = data;
                break;
              }
              await sleep(200);
            }

            if (!callRow) {
              // Final fallback: try to_number only (also with brief retry)
              for (let attempt = 1; attempt <= 3; attempt++) {
                const { data, error } = await supabase
                  .from('calls')
                  .select('to_number')
                  .eq('call_sid', callSid)
                  .maybeSingle();
                if (!error && data) {
                  toNumber = data.to_number || null;
                  break;
                }
                await sleep(150);
              }
            } else {
              dbQueueName = callRow.queue_name || dbQueueName;
              dbCompanyName = callRow.company_name || dbCompanyName;
              toNumber = callRow.to_number || null;
              console.log('[Incoming Call] ✅ Retrieved call details from database (with retry):', callRow);
            }

            // If still missing names, derive via phone number assignment -> call_queues
            if ((!dbQueueName || !dbCompanyName) && toNumber) {
              const digits = (toNumber || '').replace(/\D/g, '');
              const last10 = digits.slice(-10);
              let assignment: any = null;

              // Exact E.164 match first
              const { data: exactPQ } = await supabase
                .from('phone_queue_assignments')
                .select('queue_id, call_queues(name, company_name)')
                .eq('phone_number', toNumber)
                .maybeSingle();
              assignment = exactPQ || null;

              // Fallback to last-10
              if (!assignment && last10) {
                const { data: likePQ } = await supabase
                  .from('phone_queue_assignments')
                  .select('queue_id, call_queues(name, company_name)')
                  .like('phone_number', `%${last10}`)
                  .maybeSingle();
                assignment = likePQ || null;
              }

              if (assignment?.call_queues) {
                dbQueueName = assignment.call_queues.name || dbQueueName;
                dbCompanyName = assignment.call_queues.company_name || dbCompanyName;
              } else if (assignment?.queue_id) {
                const { data: queueRow } = await supabase
                  .from('call_queues')
                  .select('name, company_name')
                  .eq('id', assignment.queue_id)
                  .maybeSingle();
                if (queueRow) {
                  dbQueueName = queueRow.name || dbQueueName;
                  dbCompanyName = queueRow.company_name || dbCompanyName;
                }
              }
            }

            // As a last resort, take the most recent ringing call as a heuristic (single-tenant dev)
            if (!dbQueueName || !dbCompanyName) {
              const { data: recent } = await supabase
                .from('calls')
                .select('queue_name, company_name')
                .order('started_at', { ascending: false })
                .limit(1);
              if (recent && recent.length > 0) {
                dbQueueName = dbQueueName || recent[0].queue_name || undefined;
                dbCompanyName = dbCompanyName || recent[0].company_name || undefined;
              }
            }

            setState(prev => ({
              ...prev,
              queueName: (dbQueueName || null),
              companyName: (dbCompanyName || null),
            }));
          } catch (e) {
            console.error('[Incoming Call] ❌ Unexpected error looking up call details:', e);
          }
        })();
      }
      
      console.log('[Incoming Call] Final ParentCallSid:', parentCallSid);
      console.log('[Incoming Call] Final ConfName:', confName);
      console.log('[Incoming Call] Final QueueName:', queueName);
      console.log('[Incoming Call] Final CompanyName:', companyName);
      const isResumeCall = parentCallSid && isOnHoldRef.current;
      console.log('[Incoming Call] Is this a resume call?', isResumeCall);
      
      if (isResumeCall) {
        console.log('[Hold Resume] Auto-accepting resume call for', parentCallSid);
        console.log('[Hold Resume] ALWAYS UNMUTING agent on resume');
        
        // Auto-accept this call since it's resuming from hold
        activeCallRef.current = call;
        setupCallListeners(call);
        setState(prev => ({
          ...prev,
          activeCall: call,
          callState: 'connected', // Skip ringing state for resume
          incomingCallSid: anyCall?.parameters?.CallSid || null,
        }));
        
        // Accept the call first
        call.accept();
        
        // CRITICAL: ALWAYS unmute agent when resuming from hold
        // User requirement: agent should be unmuted when resume is pushed
        console.log('[Hold Resume] Setting up unmute (forced to false)');
        
        const unmuteAgent = () => {
          if (activeCallRef.current) {
            activeCallRef.current.mute(false);
            setState(prev => ({ ...prev, isMuted: false }));
            console.log('[Hold Resume] Agent unmuted');
          }
        };
        
        // Multiple attempts with increasing delays to ensure it takes effect
        setTimeout(unmuteAgent, 100);
        setTimeout(unmuteAgent, 300);
        setTimeout(unmuteAgent, 500);
        setTimeout(unmuteAgent, 1000);
        
        // Clear hold state since we're back on the call
        isOnHoldRef.current = false;
        setState(prev => ({ ...prev, isOnHold: false }));
        
        return;
      }
      
      // Regular incoming call - require manual acceptance
      activeCallRef.current = call;
      setupCallListeners(call);
      setState(prev => ({
        ...prev,
        activeCall: call,
        callState: 'ringing',
        incomingCallSid: anyCall?.parameters?.CallSid || null,
        queueName: queueName || null,
        companyName: companyName || null,
      }));

      if ('Notification' in window && Notification.permission === 'granted') {
        const notification = new Notification('Incoming Call', {
          body: `Call from ${anyCall?.parameters?.From}`,
          icon: '/favicon.ico',
          tag: 'incoming-call',
          requireInteraction: true,
        });
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
      }
    }); // Close the 'incoming' event listener

    device.on('unregistered', () => {
      console.log('Twilio Device unregistered');
      setState(prev => ({ ...prev, status: 'offline' }));
    });
  }, [setupCallListeners]);

  // Make an outbound call
  const makeCall = (phoneNumber: string, options?: { fromNumber?: string; agentId?: string }) => {
    if (!deviceRef.current) {
      console.error('Device not initialized');
      return Promise.resolve();
    }

    console.log('[useTwilioDevice] Making call to:', phoneNumber, 'from:', options?.fromNumber);
    setState(prev => ({ ...prev, callState: 'connecting', error: null }));

    // Use lowercase parameter names for Twilio TwiML compatibility
    const params: Record<string, string> = { 
      to: phoneNumber,
      phoneNumber: phoneNumber  // Also include as phoneNumber for TwiML apps that expect this
    };
    if (options?.fromNumber) {
      params.from = options.fromNumber;
    }
    if (options?.agentId) {
      params.agentId = options.agentId;
    }

    console.log('[useTwilioDevice] Call params:', params);
    return deviceRef.current.connect({ params })
      .then((call) => {
        activeCallRef.current = call;
        setupCallListeners(call);

        setState(prev => ({
          ...prev,
          activeCall: call,
          callState: 'connecting',
        }));
      })
      .catch((error) => {
        console.error('Failed to make call:', error);
        setState(prev => ({
          ...prev,
          callState: 'idle',
          error: error instanceof Error ? error.message : 'Failed to make call',
        }));
      });
  };

  // Accept incoming call
  const acceptCall = useCallback(() => {
    if (activeCallRef.current && state.callState === 'ringing') {
      activeCallRef.current.accept();
    }
  }, [state.callState]);

  // Reject incoming call
  // 🔥 CRITICAL FIX: Disconnect agent leg only, keep customer in queue
  const rejectCall = useCallback(() => {
    if (activeCallRef.current && state.callState === 'ringing') {
      console.log('[Reject] Agent rejecting call - disconnecting agent leg only (customer stays in queue)');
      
      // Use disconnect() instead of reject() to only disconnect THIS agent's leg
      // The customer remains in the conference and system will try next available agent
      activeCallRef.current.disconnect();
      
      // Clear local state
      activeCallRef.current = null;
      conferenceNameRef.current = null;
      setState(prev => ({
        ...prev,
        activeCall: null,
        callState: 'idle',
        incomingCallSid: null,
        queueName: null,
        companyName: null,
      }));
    }
  }, [state.callState]);

  // Hang up the active call
  const hangUp = useCallback(() => {
    if (activeCallRef.current) {
      activeCallRef.current.disconnect();
    }
  }, []);

  // Toggle mute on active call
  const toggleMute = useCallback(async () => {
    if (activeCallRef.current) {
      const newMuteState = !state.isMuted;
      activeCallRef.current.mute(newMuteState);
      setState(prev => ({ ...prev, isMuted: newMuteState }));
      
      // Broadcast mute status change to realtime channels so AI knows to ignore/annotate
      const parentCallSid = (activeCallRef.current.parameters as any)?.ParentCallSid;
      const callSid = (activeCallRef.current.parameters as any)?.CallSid;
      const targetCallSid = parentCallSid || callSid;
      
      if (targetCallSid) {
        try {
          // Create a transcript marker for mute events
          const muteMarker = {
            id: `${targetCallSid}-mute-${Date.now()}`,
            speaker: 'system',
            text: newMuteState ? '🔇 Agent muted (customer cannot hear agent)' : '🔊 Agent unmuted',
            timestamp: Date.now(),
            isFinal: true,
            isMuteEvent: true
          };
          
          // Broadcast to both parent and child channels
          const channels: string[] = [
            `call-${targetCallSid}`,
            parentCallSid ? `call-${parentCallSid}` : null,
            callSid ? `call-${callSid}` : null
          ].filter((ch): ch is string => ch !== null);
          
          for (const channelName of channels) {
            const channel = supabase.channel(channelName);
            await channel.send({
              type: 'broadcast',
              event: 'transcript',
              payload: muteMarker
            });
          }
          
          console.log('[Mute] Broadcasted mute status:', newMuteState, 'to channels:', channels);
        } catch (error) {
          console.error('[Mute] Failed to broadcast mute status:', error);
        }
      }
    }
  }, [state.isMuted]);

  // Toggle hold on active call with hold music
  const toggleHold = useCallback(async () => {
    if (!activeCallRef.current) return;

    // IMPORTANT: Hold only works in conference mode
    // The old hold-music API disconnects the agent, which is not what we want
    if (!conferenceNameRef.current) {
      console.warn('[Hold] Conference not ready yet - Hold function disabled until conference migration completes');
      console.warn('[Hold] Please wait ~2 seconds after call connects for conference setup to complete');
      return;
    }

    const newHoldState = !state.isOnHold;
    
    // Log ALL call parameters to debug the issue
    console.log('[Hold Debug] Full call parameters:', activeCallRef.current.parameters);
    
    // Get the CallSid - this is the agent's leg
    const agentCallSid = (activeCallRef.current.parameters as any)?.CallSid;
    
    // ParentCallSid is hidden in the Params query string, need to parse it
    const paramsString = (activeCallRef.current.parameters as any)?.Params;
    let parentCallSid: string | undefined;
    
    if (paramsString) {
      // Parse the Params query string to extract ParentCallSid
      const params = new URLSearchParams(paramsString);
      parentCallSid = params.get('ParentCallSid') || undefined;
      console.log('[Hold Debug] Parsed Params string:', paramsString);
      console.log('[Hold Debug] Extracted ParentCallSid:', parentCallSid);
    }
    
    console.log('[Hold Debug] Agent CallSid:', agentCallSid);
    console.log('[Hold Debug] ParentCallSid (customer leg):', parentCallSid);
    
    // CRITICAL: Use ParentCallSid for customer leg, NOT the agent CallSid
    // If no ParentCallSid found, this might be an outbound call where agent CallSid is correct
    const targetCallSid = parentCallSid || agentCallSid;
    
    if (!targetCallSid) {
      console.error('[Hold] ERROR: No CallSid found for hold operation');
      return;
    }
    
    console.log('[Hold] ACTION:', newHoldState ? 'PUTTING ON HOLD' : 'RESUMING FROM HOLD');
    console.log('[Hold] TARGET CallSid:', targetCallSid);
    console.log('[Hold] Using customer leg (ParentCallSid)?', !!parentCallSid);

    // For HOLD: Update hold state but DON'T mute agent
    // Agent should be able to talk to colleagues while customer is on hold
    // For RESUME: Keep hold state true until the resume call is auto-accepted
    if (newHoldState) {
      // Customer is on hold, but agent stays unmuted to talk to colleagues
      console.log('[Hold] Customer on hold - agent remains unmuted to talk to colleagues');
      
      isOnHoldRef.current = true;
      setState(prev => ({ ...prev, isOnHold: true }));
      console.log('[Hold] State updated - isOnHold: true');
      
      // DO NOT mute agent - they need to talk to colleagues!
    } else {
      // Keep isOnHoldRef true so incoming resume call can be detected and auto-accepted
      // The auto-accept handler will clear it after accepting the call
      console.log('[Hold] Initiating resume - keeping hold state true until resume call accepted');
      setState(prev => ({ ...prev, isOnHold: false })); // Update UI to show resume in progress
      
      // Note: Agent will be unmuted by the resume call handler
    }

    try {
      const { data: authData } = await supabase.auth.getSession();
      if (!authData.session) {
        throw new Error('Not authenticated');
      }

      // Use conference-hold API
      console.log('[Hold] Using conference-hold API for conference:', conferenceNameRef.current);
      
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/twilio-conference-hold`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authData.session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conferenceName: conferenceNameRef.current,
          customerCallSid: targetCallSid,
          action: newHoldState ? 'hold' : 'unhold',
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to toggle hold in conference');
      }

      const result = await response.json();
      console.log('[Conference Hold] Success:', result);
      
      // Broadcast hold/resume event to realtime channels for transcript
      try {
        const holdMarker = {
          id: `${targetCallSid}-hold-${Date.now()}`,
          speaker: 'system',
          text: newHoldState ? '⏸️ Call placed on hold (customer hears music)' : '▶️ Call resumed',
          timestamp: Date.now(),
          isFinal: true,
          isHoldEvent: true
        };
        
        // Broadcast to both parent and child channels
        const channels: string[] = [
          `call-${targetCallSid}`,
          parentCallSid ? `call-${parentCallSid}` : null,
          agentCallSid ? `call-${agentCallSid}` : null
        ].filter((ch): ch is string => ch !== null);
        
        for (const channelName of channels) {
          const channel = supabase.channel(channelName);
          await channel.send({
            type: 'broadcast',
            event: 'transcript',
            payload: holdMarker
          });
        }
        
        console.log('[Hold] Broadcasted hold status:', newHoldState, 'to channels:', channels);
      } catch (broadcastError) {
        console.error('[Hold] Failed to broadcast hold status:', broadcastError);
      }
    } catch (error) {
      console.error('Failed to toggle hold:', error);
      // Revert state on error
      if (newHoldState) {
        isOnHoldRef.current = false;
        setState(prev => ({ ...prev, isOnHold: false }));
      } else {
        isOnHoldRef.current = true;
        setState(prev => ({ ...prev, isOnHold: true }));
      }
    }
  }, [state.isOnHold, state.isMuted]);

  // Send DTMF digits during a call
  const sendDigits = useCallback((digits: string) => {
    if (activeCallRef.current) {
      activeCallRef.current.sendDigits(digits);
    }
  }, []);

  // Attach to existing global device if another part of the app already initialized it
  useEffect(() => {
    const globalDevice = (window as any).__twilioDevice as Device | undefined;
    if (globalDevice && !deviceRef.current) {
      deviceRef.current = globalDevice;
      registerDeviceListeners(globalDevice);
      setState(prev => ({ ...prev, device: globalDevice, status: 'ready' }));
    }
  }, [registerDeviceListeners]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (activeCallRef.current) {
        activeCallRef.current.disconnect();
      }
      if (deviceRef.current) {
        deviceRef.current.unregister();
        deviceRef.current.destroy();
      }
    };
  }, []);

  return [
    state,
    {
      initializeDevice,
      makeCall,
      acceptCall,
      rejectCall,
      hangUp,
      toggleMute,
      toggleHold,
      sendDigits,
    },
  ];
};
